<template>
  <div class="whatsapp-modal">
    <div class="whatsapp-modal__header">
      <h1 class="whatsapp-modal__modal_title"> Integrar WhatsApp Business</h1>
    </div>
    <div class="whatsapp-modal__body">
      <div class="whatsapp-modal__sidebar">
        <a-steps :current="currentStep" direction="vertical" class="whatsapp-steps">
          <a-step>
            <template #title>
              Crear WhatsApp Business
            </template>

            <template #description>
              <div>
                <div :class="['whatsapp-steps__subitem', { 'is-active': currentSubStep === 0 }]">Nombre de cuenta</div>
                <div :class="['whatsapp-steps__subitem', { 'is-active': currentSubStep === 1 }]">Datos de administrador</div>
              </div>
            </template>
          </a-step>
          <a-step>
            <template #title>
              Configurar en Facebook
            </template>
            <template #description>
              <div class="whatsapp-steps__spacer"></div>
            </template>
          </a-step>
          <a-step v-if="currentStep === 2" title="Configuración abierta" />
        </a-steps>
        <div class="alert-wrapper">
          <a-alert type="info">
            <template #message>
              <VideoLibraryIcon style="margin-bottom: 6px;"/>
              <p style="margin-bottom: 2px;">¿Tienes dudas?</p>
            </template>
            <template #description>
              <p style="margin-bottom: 4px;">Mira nuestro tutorial con el paso a paso.</p>
              <a href="https://www.youtube.com/watch?v=Zu-ilRogMYI" target="_blank" rel="noopener noreferrer">
                <span style="display: inline-flex; gap: 2px; align-items: center;">
                  Ver videotutorial
                  <IconExternalLink color="currentColor" style="width: 14px;" />
                </span>
              </a>
            </template>
          </a-alert>
        </div>
      </div>
      <div class="whatsapp-modal__main">
        <div class="whatsapp-modal__content">
          <a-form ref="formRef" layout="vertical" :model="formState" validate-trigger="change">
            <div v-show="currentStep === 0 && currentSubStep === 0">
              <div class="whatsapp-modal__image">
                <img src="@/app/channels/components/image/whatsapp_logo.png"
                  alt="whatsapp-user-image" />
              </div>
              <h1 class="whatsapp-modal__title">Ingresa un nombre para tu cuenta de WhatsApp Business</h1>
              <a-form-item class="form-item--compact" name="name_account" 
                :rules="[{ required: true, message: '' }]"
                required
              >
                <template #label>
                  <div class="whatsapp-modal__label">
                    <span>Nombre de cuenta WhatsApp Business</span>
                    <a-tooltip>
                      <template #title>
                        <div>
                          Este nombre será el alias para identificar la cuenta en cualquier módulo de BeexCC.
                        </div>
                      </template>

                      <InfoCircleOutlined class="info-icon" />
                    </a-tooltip>
                  </div>
                </template>

                <a-input v-model:value="formState.name_account"
                  @keypress="(e: KeyboardEvent) => handleKeyPress('alphanumeric', e)"
                  @input="(e: Event) => handleInput('name_account', 'alphanumeric', e)"
                  @paste="(e: ClipboardEvent) => handlePaste('name_account', 'alphanumeric', e)"
                  placeholder="Ejemplo: Nombre de tu empresa"
                />
              </a-form-item>

              <ul class="whatsapp-modal__rules">
                <li v-for="(rule, i) in rules" :key="i">
                  <component :is="getIcon(ruleStatus[rule.key])" class="icon icon--lg"
                    :class="{
                      'icon--success': ruleStatus[rule.key] === 'success',
                      'icon--error': ruleStatus[rule.key] === 'error'
                    }"
                  />
                  <span>{{ rule.text }}</span>
                </li>
              </ul>

              <a-alert class="whatsapp-modal__alert" type="warning" show-icon
                message="Ten en cuenta que no podrás editar este nombre después de crear la cuenta." />
            </div>
            <div v-show="currentStep === 0 && currentSubStep === 1">
              <div class="whatsapp-modal__image">
                <img src="@/app/channels/components/image/whatsapp_admin.png"
                  alt="whatsapp-user-image" />
              </div>
              <h2 class="whatsapp-modal__title">Ingresa los datos de contacto del administrador de WhatsApp Business</h2>
              <a-form-item label="Nombre y apellidos" name="full_name"
                :rules="[
                  { required: true, message: 'Nombre y apellidos inválidos' },
                  {
                    pattern: /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/,
                    message: 'Solo se permiten letras'
                  }
                ]"
                required
              >
                <a-input v-model:value="formState.full_name"
                  @keypress="(e: KeyboardEvent) => handleKeyPress('letters', e)"
                  @input="(e: Event) => handleInput('full_name', 'letters', e)"
                  @paste="(e: ClipboardEvent) => handlePaste('full_name', 'letters', e)"
                  placeholder="Ingresar nombres y apellidos"
                />
              </a-form-item>
              <a-form-item label="Correo electrónico" name="email"
                :rules="[
                  { required: true, message: 'Correo electrónico inválido' },
                  { type: 'email', message: 'Correo electrónico inválido' }
                ]"
                required
              >
                <a-input v-model:value="formState.email" placeholder="Ingresar correo electrónico"/>
              </a-form-item>
              <a-form-item label="Teléfono de contacto" name="phone_number"
                :rules="[
                  { required: true, message: 'Teléfono de contacto inválido' },
                  {
                    pattern: /^[0-9]+$/,
                    message: 'Solo se permiten números'
                  }
                ]"
                required
              >
                <a-row :gutter="16" type="flex">
                  <a-col span="6">
                    <selector-country :code="code_phone" @selected="code_phone = $event" />
                  </a-col>
                  <a-col flex="1" span="18">
                    <a-input
                      v-model:value="formState.phone_number"
                      @keypress="(e: KeyboardEvent) => handleKeyPress('numbers', e)"
                      @input="(e: Event) => handleInput('phone_number', 'numbers', e)"
                      @paste="(e: ClipboardEvent) => handlePaste('phone_number', 'numbers', e)"
                      placeholder="Ingresar número de teléfono"
                    />
                  </a-col>
                </a-row>
              </a-form-item>
              <a-alert class="whatsapp-modal__alert whatsapp-modal__alert__--top-margin" type="warning" show-icon
                message="Asegúrate de ingresar los datos correctos, ya que no podrás editarlos después de crear la cuenta." />
            </div>
            <div v-show="currentStep === 1" class="whatsapp-modal__fb">
              <div class="whatsapp-modal__image">
                <img src="@/app/channels/components/image/whatsapp_settings.png"
                  alt="whatsapp-user-image" />
              </div>
              <h2 class="whatsapp-modal__title whatsapp-modal__title--small-margin">Cuenta de WhatsApp creada.<br />Configúrala en Facebook para activarla</h2>
              <p class="whatsapp-modal__fb-description">Tu canal de WhatsApp fue creado pero requiere ser configurado en Facebook Business Manager para activarla. Puedes hacerlo ahora o retomarlo después desde Mis canales.</p>

              <p class="whatsapp-modal__fb-subtitle"> De hacerlo ahora, asegúrate de: </p>

              <ul class="whatsapp-modal__fb-rules">
                <li>Aceptar los términos y condiciones de Gupshup y WhatsApp</li>
                <li>Usar la cuenta de administrador de Facebook Business Manager.</li>
                <li>Usar la cuenta donde está registrado el ID de la empresa.</li>
                <li>Autorizar a Gupshup para administrar tu WhatsApp Business.</li>
              </ul>
              <a-alert class="whatsapp-modal__alert" type="warning" show-icon>
                <template #message>
                  <strong>Importante:</strong>
                  Meta puede aprobar y activar tu cuenta de WhatsApp Business dentro de <strong>máximo 72 horas.</strong>
                </template>
              </a-alert>
              <a-button class="whatsapp-modal__fb-button" type="primary" @click="configureWhatsappInFacebook">Configurar Whatsapp en Facebook <ExternalLinkIcon class="icon icon--fb"/></a-button>
            </div>
            <div v-show="currentStep === 2" class="whatsapp-modal__fb">
              <div class="whatsapp-modal__image">
                <img src="@/app/channels/components/image/facebook_logo.png"
                  alt="whatsapp-user-image" />
              </div>
              <h2 class="whatsapp-modal__title whatsapp-modal__title--small-margin">La configuración en Facebook se abrió en una ventana externa</h2>
              <p class="whatsapp-modal__fb-description">¡Solo queda un paso! Para que tu cuenta de WhatsApp Business sea activada, debes configurarla en Facebook primero:</p>
              <ul class="whatsapp-modal__fb-rules-2">
                <li class="rule-item">
                  <CheckCircleOutlined class="icon icon--rule icon--success" />
                  <span class="rule-text">
                    Si completaste la configuración, el estado de tu solicitud se actualizará a Pendiente.
                    Meta revisará los datos para poder aprobar y activar la cuenta en <strong>máximo 72 horas.</strong>
                  </span>
                </li>

                <li class="rule-item">
                  <CloseCircleOutlined class="icon icon--rule icon--error" />
                  <span class="rule-text">
                    Si no completaste la configuración, tu solicitud quedará en estado Incompleto.
                    Podrás retomarla después en el módulo Mis canales.
                    <strong>Hasta entonces, no podrás integrar otra cuenta.</strong>
                  </span>
                </li>
              </ul>
              <a-button class="whatsapp-modal__channel-button" type="primary" @click="toChannels">Ir a Mis canales</a-button>
            </div>
          </a-form>
        </div>
      </div>
    </div>
    <div v-show="currentStep === 0 && currentSubStep === 0" class="whatsapp-modal__footer">
      <a-button @click="handleCancel">Cancelar</a-button>
      <a-tooltip :title="!isCompleteStep1 ? 'Completa los campos requeridos correctamente para continuar.' : ''">
        <span>
          <a-button
            type="primary"
            :disabled="!isCompleteStep1"
            @click="goStep2"
          >
            Continuar
          </a-button>
        </span>
      </a-tooltip>
    </div>
    <div v-show="currentStep === 0 && currentSubStep === 1" class="whatsapp-modal__footer">
      <a-button
        :disabled="isLoading"
        @click="goStep1"
      >
        Regresar
      </a-button>
      <a-tooltip :title="!isFormValid ? 'Completa todos los campos requeridos para crear la cuenta.' : ''">
        <span>
          <a-button
            type="primary"
            :disabled="!isFormValid || isLoading"
            @click="goStep3"
          >
            Crear cuenta y continuar
          </a-button>
        </span>
      </a-tooltip>
    </div>
  </div>
</template>

<script setup lang="ts">
import SelectorCountry from "@/app/shared/components/selectors/SelectorCountry.vue";
import { InfoCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, InfoCircleFilled } from "@ant-design/icons-vue";
import { Modal } from "ant-design-vue";
import CircleDashedIcon from "@/app/common/components/icons/CircleDashedIcon.vue";
import ExternalLinkIcon from "@/app/common/components/icons/ExternalLinkIcon.vue";
import VideoLibraryIcon from "@/app/common/components/icons/VideoLibraryIcon.vue";
import IconExternalLink from "@/app/common/components/atoms/IconExternalLink.vue";
import { ref, reactive, computed, createVNode, watch } from "vue";
import { useRouter } from "vue-router";

import { message } from "ant-design-vue";
import { createAccountWithContact, getEmbedLink, validateAccountName } from "@/app/channels/services/whatsappAutoSetup";
import { ResponseError } from "@/app/shared/utils/httpManager";

const rules: {key: RuleKey; text: string} [] = [
  { key: 'length', text: "Mínimo 6 caracteres de longitud" },
  { key: 'format', text: "Solo letras y/o números, minúsculas y mayúsculas"},
  { key: 'unique', text: "Nombre único"}
]

const router = useRouter();

const formRef = ref();
const isFormValid = ref(false);
const isLoading = ref(false);

const hasInteractedStep2 = ref(false);

const currentStep = ref(0);
const currentSubStep = ref(0);

const code_phone = ref(1);

const gupshupAppId = ref("");

const initialStateForm = {
  name_account: "",
  full_name: "",
  email: "",
  phone_number: "",
}

const initialRulesStatus = {
  length: '',
  format: '',
  unique: '',
}

const formState = reactive({ ...initialStateForm });
const ruleStatus = reactive({ ...initialRulesStatus });

const resetForm = () => {
  Object.assign(formState, initialStateForm);
  Object.assign(ruleStatus, initialRulesStatus);
  hasInteractedStep2.value = false;
  currentStep.value = 0;
  currentSubStep.value = 0;
  formRef.value.resetFields();
}

type FormState = typeof formState
type RuleKey = 'length' | 'format' | 'unique'

const isCompleteStep1 = computed(() => {
  return !!formState.name_account;
})

const validateForm = async () => {
  if (!hasInteractedStep2.value) return;
  try {
    await formRef.value.validate()
    isFormValid.value = true
  } catch {
    isFormValid.value = false
  }
}

const getIcon = (status: string | null) => {
  if (status === 'success') return CheckCircleOutlined
  if (status === 'error') return CloseCircleOutlined
  return CircleDashedIcon
};

const REGEX = {
  letters: /[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g,
  numbers: /[^0-9]/g,
  alphanumeric: /[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s]/g
}

const REGEX_KEY = {
  letters: /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]$/,
  numbers: /^[0-9]$/,
  alphanumeric: /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s]$/
}

const handleInput = (field: keyof FormState, type: 'letters' | 'numbers' | 'alphanumeric', e: Event) => {
  const input = e.target as HTMLInputElement
  const cleaned = input.value.replace(REGEX[type], '')
  formState[field] = cleaned
};

const handlePaste = (field: keyof FormState, type: 'letters' | 'numbers' | 'alphanumeric', e: ClipboardEvent) => {
  e.preventDefault()
  const paste = e.clipboardData?.getData('text') || ''
  const cleaned = paste.replace(REGEX[type], '')
  formState[field] += cleaned
};

const handleKeyPress = (type: 'letters' | 'numbers' | 'alphanumeric', e: KeyboardEvent) => {
  if (!REGEX_KEY[type].test(e.key)) {
    e.preventDefault()
  }
};

const emit = defineEmits<{
  (e: "cancel" ): void;
  (e: "close"): void;
}>();

const handleCancel = () => {
  emit("cancel");
};

const goStep1 = () => {
  currentStep.value = 0;
  currentSubStep.value = 0;
};

const validateLocalAccountName = () => {
  const value = formState.name_account

  ruleStatus.length = value.length >= 6 ? 'success' : 'error'
  ruleStatus.format = /^[a-zA-Z0-9 ]+$/.test(value) && value.trim().length > 0 ? 'success' : 'error'

  return (
    ruleStatus.length === 'success' &&
    ruleStatus.format === 'success'
  )
}

const checkAccountName = async () => {
  const isLocalValid = validateLocalAccountName();

  if (!isLocalValid) return false;

  try {
    ruleStatus.unique = ''
    const response = await validateAccountName(formState.name_account);
    ruleStatus.unique = response?.is_unique ? 'success' : 'error';
  } catch (error) {
    ruleStatus.unique = 'error'
  }

  return Object.values(ruleStatus).every(
    status => status === 'success'
  )
}

const goStep2 = async () => {
  const validAccountName = await checkAccountName();
  if (!validAccountName) return;

  hasInteractedStep2.value = true;
  currentStep.value = 0;
  currentSubStep.value = 1;
};

const goStep3 = async () => {
  if (isLoading.value) return;

  let createdSuccessfuly = false;
  try {
    isLoading.value = true;
    const payload = {
      name: formState.name_account,
      email: formState.email,
      contact_name: formState.full_name,
      contact_number: formState.phone_number,
      country: code_phone.value,
    }
    const response = await createAccountWithContact(payload);
    gupshupAppId.value = response.app.gupshup_app_id;

    message.success("Cuenta de WhatsApp Business creada con éxito");
    createdSuccessfuly = true;
  } catch (error) {
    let messageError = "Ha ocurrido un error"
    if (error instanceof ResponseError) {
      const detailData = error?.data?.detail?.data;
      if (detailData) {
        messageError = detailData;
      }
      else {
        messageError = error.getErrorMessage();
      }
    }
    message.error(messageError);
    createdSuccessfuly = false;
  } finally {
    isLoading.value = false;
  }

  if (!createdSuccessfuly) return;

  currentStep.value = 1;
  currentSubStep.value = -1;
};

const configureWhatsappInFacebook = async () => {
  try {
    currentStep.value = 2;
    const embedLink = await getEmbedLink(gupshupAppId.value);
    window.open(embedLink.link, '_blank', 'noopener,noreferrer');
  }
  catch (error) {
    const messageError = "Error al generar el enlace de configuración. Inténtalo más tarde.";
    message.error(messageError);
  }
};

const toChannels = () => {
  router.push("/channels/board/");
};

const showLostProgressModal = () => {
  Modal.confirm({
    title: "¿Deseas descartar tu progreso realizado?",
    icon: createVNode(ExclamationCircleOutlined),
    content: "Al descartar, tu progreso no quedará guardado. Recuerda que podrás iniciarlo de nuevo cuando lo desees.",
    okText: "Sí, descartar",
    cancelText: "No, cancelar",
    centered: true,
    onOk() {
      emit("close");
    },
  });
};

const showIncompleteRequestModal = () => {
  Modal.confirm({
    title: "Al cerrar, tu solicitud de WhatsApp Business quedará incompleta hasta que la retomes.",
    icon: createVNode(ExclamationCircleOutlined),
    content: createVNode('div', {}, [
      'Para ver el registro de esta solicitud, dirígete al módulo ',
      createVNode('span', { style: 'font-weight: 600' }, 'Mis canales'),
      '. Recuerda que podrás completar su configuración desde ahí cuando lo necesites.'
    ]),
    okText: "Ir a Mis canales",
    cancelText: "Continuar configuración",
    centered: true,
    onOk() {
      toChannels();
    },
  });
};

const showOpenConfigurationModal = () => {
  Modal.confirm({
    title: "Recuerda que podrás revisar el estado de tu solicitud de WhatsApp Business desde Mis canales.",
    wrapClassName: "modal-open-configuration",
    icon: createVNode(InfoCircleFilled, {
      style: 'color: #1677ff',
    }),
    okText: "Ver en Mis canales",
    centered: true,
    onOk() {
      toChannels();
    },
    cancelButtonProps: { style: { display: 'none' } } as any
  });
};

const showModal = () => {
  if (currentStep.value === 0) {
    showLostProgressModal();
  }
  else if (currentStep.value === 1) {
    showIncompleteRequestModal();
  }
  else if (currentStep.value === 2) {
    showOpenConfigurationModal();
  }
}

const isDirty = () => {
  return !!(
    formState.name_account ||
    formState.full_name ||
    formState.email ||
    formState.phone_number
  )
};

watch(formState, validateForm, { deep: true, immediate: true })

watch(
  () => formState.name_account,
  () => validateLocalAccountName()
)

defineExpose({
  isDirty,
  showModal,
  resetForm,
})

</script>

<style lang="sass" scoped>
.whatsapp-modal
  display: flex
  flex-direction: column
  width: 100%
  height: 100%
  &__modal_title
    font-weight: 600
    font-style: normal
    font-size: 16px
    line-height: 24px !important
    text-align: left
    padding: 12px 24px
  &__header
    border-bottom: 1px solid #f0f0f0
    width: 100%
  &__body
    flex: 1
    display: flex
    flex-direction: row
    font-weight: 400
    font-size: 14px
    line-height: 22px
    letter-spacing: 0
  &__sidebar
    border-right: 1px solid #f0f0f0
    padding: 24px
    width: 30%
    flex-shrink: 0
    display: flex
    flex-direction: column
  &__main
    background: linear-gradient(180deg, #F7FFF0 0%, #FFFFFF 27%)
    flex: 1
    display: flex
    flex-direction: column
    align-items: center
  &__content
    width: 100%
    max-width: 480px
  &__footer
    border-top: 1px solid #f0f0f0
    padding: 16px 24px
    display: flex
    justify-content: flex-end
    gap: 8px
  &__title
    font-weight: 600
    font-size: 24px
    line-height: 32px
    letter-spacing: 0
    text-align: center
    padding: 0px 24px
    margin-bottom: 28px
    &--small-margin
      margin-bottom: 12px
  &__label
    color: #262626E0
    display: inline-flex
    align-items: center
    gap: 6px
    .info-icon
      color: #262626E0
  &__rules
    text-align: left
    color: #00000073
    list-style: none
    padding: 0
    margin: 0
    display: flex
    flex-direction: column
    gap: 9px
    margin-bottom: 24px
    li
      display: flex
      align-items: center
      gap: 8px

  &__alert
    padding: 8px 24px
    gap: 4px
    strong
      font-weight: 600
    &--top-margin
      margin-top: 30px

  &__image
    text-align: center
    margin: 32px 0px 16px 0px
    img
      max-width: 64px
  &__fb
    display: flex
    flex-direction: column
    align-items: center
  &__fb-description
    text-align: center
    font-weight: 400
    margin-top: 0px
    color: #000000A6
    padding: 0px 4px
    margin-bottom: 20px
  &__fb-subtitle
    text-align: center
    font-weight: 600
    color: #000000A6
    margin-bottom: 4px
  &__fb-rules
    text-align: left
    color: #00000073
    margin-bottom: 20px
    width: 100%
  &__fb-rules-2
    display: flex
    flex-direction: column
    list-style: none
    padding: 0px 12px
    margin: 4px 0px 0px 0px
    gap: 24px
  &__fb-button
    margin-top: 24px
    font-size: 16px
    height: 40px
    width: 100%
    max-width: 320px
    display: inline-flex
    align-items: center
    justify-content: center
  &__channel-button
    margin-top: 32px
    font-size: 14px
    height: 32px
    width: 100%
    max-width: 134px
  :deep(.form-item--compact)
    margin-bottom: 12px

  .alert-wrapper
    margin-top: auto
    :deep(.ant-alert)
      width: 100%
      text-align: left
      padding: 16px

    :deep(.ant-alert-message)
      margin-bottom: 2px
      font-size: 14px
      font-weight: 600
      line-height: 22px

.icon
  width: 20px
  height: 20px
  flex-shrink: 0
  display: flex
  align-items: center
  justify-content: center
  margin-top: 2px
.icon--lg
    :deep(svg)
      width: 17.5px
      height: 17.5px
.icon--rule
  height: 24px
  font-size: 17.5px
.icon--success
  color: #389E0D
.icon--error
  color: #CF1322
.icon--fb
  width: 16px
  height: 16px

.rule-item
  display: flex
  align-items: flex-start
  gap: 8px
  color: #000000A6
  .rule-text
    padding-right: 4px
  strong
      font-weight: 600

.whatsapp-steps
  :deep(.ant-steps-item-title)
    padding-right: 0
  &__subitem
    color: #00000073
    margin: 4px 0px
    &.is-active
      color: #000000e0
  &__spacer
    min-height: 20px

:global(.modal-open-configuration .ant-modal-confirm-body)
  align-items: flex-start !important
:global(.modal-open-configuration .anticon)
  margin-top: 3px !important

</style>
